import React, { useMemo, useState } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import style from './CareerCompanion.module.css';
import Cookies from 'js-cookie';
const mapContainerStyle = {
    width: '100%',
    height: '400px',
};

const defaultCenter = { lat: 37.7749, lng: -122.4194 }; // fallback: San Francisco

const MapExplorer = ({
    jobProfile,
    setJobProfile,
    availableRoles,
    location,
    setLocation,
    companies,
    setCompanies,
    apiUrl
}) => {
    const [loading, setLoading] = useState(false);

    // Load Google Maps JS API
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    const handleSearchCompanies = () => {
        if (!jobProfile) return;
        setLoading(true);

        const token = Cookies.get('token');   // 👈 get JWT

        fetch(`${apiUrl}/companies`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}), // 👈 send token
            },
            body: JSON.stringify({ job_profile: jobProfile, location }),
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to fetch companies');
                }
                return res.json();
            })
            .then((data) => {
                setCompanies(data || []);
            })
            .catch((err) => {
                console.error(err);
                // keep old companies if you want, or clear:
                // setCompanies([]);
            })
            .finally(() => setLoading(false));
    };

    // Compute map center from companies (fallback if none)
    const mapCenter = useMemo(() => {
        if (companies.length > 0 && companies[0].location) {
            return {
                lat: companies[0].location.lat,
                lng: companies[0].location.lng,
            };
        }
        return defaultCenter;
    }, [companies]);

    return (
        <div className={style.section}>
            <h2>Find Jobs Near You</h2>
            <p style={{ marginBottom: '12px' }}>
                Pick a target role and location, then discover companies hiring near you.
            </p>

            {/* Role selector */}
            <div className={style.mapControls}>
                <div className={style.inputGroup}>
                    <label className={style.label}>Suggested Roles</label>
                    <select
                        className={style.select}
                        value={jobProfile || ''}
                        onChange={(e) => setJobProfile(e.target.value || null)}
                    >
                        <option value="">Select a role</option>
                        {availableRoles?.map((role, idx) => (
                            <option key={idx} value={role}>
                                {role}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Location input */}
                <div className={style.inputGroup}>
                    <label className={style.label}>Location</label>
                    <input
                        className={style.input}
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="Enter Location"
                    />
                </div>

                {/* Search button */}
                <button
                    onClick={handleSearchCompanies}
                    className={style.button}
                    disabled={loading || !jobProfile}
                >
                    {loading ? 'Searching...' : 'Search Companies'}
                </button>
            </div>

            {/* MAP SECTION */}
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                {loadError && <p>Could not load map. Check your API key.</p>}
                {!isLoaded && !loadError && <p>Loading map...</p>}

                {isLoaded && (
                    <div className={style.mapWrapper}>
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={companies.length > 0 ? 11 : 3}
                        >
                            {companies.map((c, i) =>
                                c.location ? (
                                    <Marker
                                        key={i}
                                        position={{
                                            lat: c.location.lat,
                                            lng: c.location.lng,
                                        }}
                                        title={c.name}
                                    />
                                ) : null
                            )}
                        </GoogleMap>
                    </div>
                )}
            </div>

            {/* LIST SECTION */}
            {companies.length > 0 ? (
                <div className={style.companiesList}>
                    <p>
                        Found {companies.length} companies hiring for{' '}
                        <strong>{jobProfile}</strong> near <strong>{location}</strong>:
                    </p>
                    <div className={style.grid}>
                        {companies.map((c, i) => (
                            <div key={i} className={style.companyCard}>
                                <h4>{c.name}</h4>
                                <p>{c.address}</p>
                                {c.rating && <p>Rating: {c.rating} ⭐</p>}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className={style.placeholder}>
                    <p>
                        {jobProfile
                            ? 'Adjust role or location, then click "Search Companies" to refresh results.'
                            : 'Select a suggested role above to start searching for companies near you.'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default MapExplorer;
